/**
 * Jack & Jones — collection grid (sections/jj-collection-grid.liquid).
 *
 * Horizon does the heavy lifting (filtering, sorting and infinite scroll re-render the section
 * through facets.js and results-list.js). This file only drives the brand layer:
 * - the filters panel: a 344px sidebar on desktop (dialog.show()), a full-screen modal on mobile
 *   (dialog.showModal()); Escape closes both and the focus goes back to FILTRAR (rule D4);
 * - the «Ordenar por» panel (snippets/jj-plp-sort.liquid): a modal; on desktop the radio is only a
 *   choice until APLICAR, on mobile the tap applies it. Closed without applying, the choice goes back
 *   to the current sort, so a later filter change doesn't carry it;
 * - the 3 / 4 columns view selector, remembered for the session;
 * - «VER MÁS» in long filter lists and the two-thumb price slider;
 * - desktop: la barra y el panel de filtros abierto quedan fijos al scrollear (CSS sticky); acá sólo se
 *   mide cuánto deja fijo el header arriba (--jj-plp-sticky-top);
 * - mobile: el panel de filtros y «Ordenar por» salen deslizando a la derecha antes de cerrarse;
 * - the quick buy of the cards: sizes + button over the photo on desktop, in a sheet on mobile.
 *   Choosing a size, adding to the cart and the mobile sheet live in jj-quick-buy.js (shared with
 *   the search cards and the product carousel).
 *
 * All listeners are delegated on the document, so cards appended by the infinite scroll and
 * markup re-rendered by a filter work without re-binding.
 */
import { lockScroll, unlockScroll, prefersReducedMotion } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';
import '@theme/jj-quick-buy';

const desktopQuery = window.matchMedia('(min-width: 750px)');
const VIEW_KEY = 'jj-plp-view';

/** @param {Element} element */
const sectionOf = (element) => element.closest('.shopify-section');

/**
 * Cierra un diálogo de la sección. En mobile el panel de filtros y «Ordenar por» salen deslizando a la
 * derecha (Asana 1219757068680842; [data-jj-closing] en section-jj-collection-grid.css) y recién al
 * terminar se cierran, así el evento close (scroll, foco) llega después. En desktop o con movimiento
 * reducido cierran en el acto. Si un re-render le saca la marca a mitad de camino, el timer lo cierra.
 * @param {HTMLDialogElement} dialog
 * @returns {Promise<void>} resuelve cuando el diálogo ya está cerrado
 */
function closeDialog(dialog) {
  if (!dialog.open) return Promise.resolve();
  if (desktopQuery.matches || prefersReducedMotion()) {
    dialog.close();
    return Promise.resolve();
  }
  if (dialog.hasAttribute('data-jj-closing')) return Promise.resolve();

  return new Promise((resolve) => {
    /** @param {AnimationEvent} event */
    const onEnd = (event) => {
      if (event.target === dialog && !event.pseudoElement) finish();
    };
    const finish = () => {
      window.clearTimeout(timer);
      dialog.removeEventListener('animationend', onEnd);
      dialog.removeAttribute('data-jj-closing');
      dialog.close();
      resolve();
    };
    const timer = window.setTimeout(finish, 300);
    dialog.addEventListener('animationend', onEnd);
    dialog.setAttribute('data-jj-closing', '');
  });
}

/* ----------------------------------------------------------------------------------------------
 * Filters panel
 * ------------------------------------------------------------------------------------------- */

/** @type {HTMLElement | null} */
let filtersTrigger = null;

/** @param {Element} root */
function syncFiltersToggle(root) {
  const panel = root.querySelector('[data-jj-filters]');
  const open = panel instanceof HTMLDialogElement && panel.open;
  root.querySelectorAll('[data-jj-filters-toggle]').forEach((toggle) => {
    toggle.setAttribute('aria-expanded', String(open));
  });
  sectionOf(root)?.toggleAttribute('data-jj-filters-open', open);
}

/**
 * @param {Element} root
 * @param {HTMLElement | null} trigger
 */
function openFilters(root, trigger) {
  const panel = root.querySelector('[data-jj-filters]');
  if (!(panel instanceof HTMLDialogElement) || panel.open) return;

  filtersTrigger = trigger;
  if (desktopQuery.matches) {
    panel.show();
  } else {
    panel.showModal();
    lockScroll(panel);
  }
  syncFiltersToggle(root);

  const title = panel.querySelector('[data-jj-filters-title]');
  if (title instanceof HTMLElement) title.focus({ preventScroll: true });
}

/** @param {HTMLDialogElement} panel */
function closeFilters(panel) {
  closeDialog(panel);
}

/* ----------------------------------------------------------------------------------------------
 * «Ordenar por» panel
 * ------------------------------------------------------------------------------------------- */

/** @type {HTMLElement | null} */
let sortTrigger = null;

/**
 * Checks the current sort again (the one the page was rendered with). The search also has the
 * accordion of its filters panel in the same radio group, which can leave the panel without a check.
 * @param {HTMLDialogElement} panel
 */
function resetSortChoice(panel) {
  panel.querySelectorAll('.jj-sort-panel__radio').forEach((radio) => {
    if (radio instanceof HTMLInputElement && radio.defaultChecked) radio.checked = true;
  });
}

/** @param {HTMLElement} toggle */
function openSort(toggle) {
  const panel = document.getElementById(toggle.getAttribute('aria-controls') || '');
  if (!(panel instanceof HTMLDialogElement) || panel.open) return;

  sortTrigger = toggle;
  delete panel.dataset.jjApplied;
  resetSortChoice(panel);
  panel.showModal();
  lockScroll(panel);
  toggle.setAttribute('aria-expanded', 'true');

  const checked = panel.querySelector('.jj-sort-panel__radio:checked') || panel.querySelector('.jj-sort-panel__radio');
  if (checked instanceof HTMLElement) checked.focus({ preventScroll: true });
}

/**
 * Applies the chosen sort: through the facets form of the section (Horizon re-renders the grid) or,
 * without a filters panel, by loading the URL with sort_by.
 * @param {HTMLDialogElement} panel
 */
function applySort(panel) {
  const checked = panel.querySelector('.jj-sort-panel__radio:checked');
  const changed = checked instanceof HTMLInputElement && !checked.defaultChecked;

  panel.dataset.jjApplied = 'true';
  // Mobile: el orden se aplica cuando el modal terminó de salir, para que el re-render no lo agarre a
  // mitad de la animación
  closeDialog(panel).then(() => {
    if (changed && checked instanceof HTMLInputElement) submitSort(checked);
  });
}

/**
 * @param {HTMLInputElement} checked
 */
function submitSort(checked) {
  const form = checked.form;
  const facetsForm = form?.closest('facets-form-component');
  if (facetsForm && 'updateFilters' in facetsForm && typeof facetsForm.updateFilters === 'function') {
    facetsForm.updateFilters();
    return;
  }

  // El orden por defecto va sin sort_by, como en el formulario de filtros (facets.js)
  const url = new URL(window.location.href);
  const panel = checked.closest('[data-jj-sort-panel]');
  if (panel instanceof HTMLElement && panel.dataset.defaultSortBy === checked.value) {
    url.searchParams.delete('sort_by');
  } else {
    url.searchParams.set('sort_by', checked.value);
  }
  url.searchParams.delete('page');
  window.location.assign(url.toString());
}

/**
 * Mobile: the tap on an option applies it (Figma 921:56566 has no APLICAR).
 * @param {Event} event
 */
function onSortChange(event) {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.matches('.jj-sort-panel__radio')) return;
  if (desktopQuery.matches) return;
  const panel = input.closest('[data-jj-sort-panel]');
  if (panel instanceof HTMLDialogElement) applySort(panel);
}

/** @param {Event} event */
function onDialogClose(event) {
  const dialog = event.target;
  if (!(dialog instanceof HTMLDialogElement)) return;

  if (dialog.matches('[data-jj-sort-panel]')) {
    unlockScroll(dialog);
    if (!dialog.dataset.jjApplied) resetSortChoice(dialog);
    delete dialog.dataset.jjApplied;
    document.querySelectorAll(`[data-jj-sort-toggle][aria-controls="${dialog.id}"]`).forEach((toggle) => {
      toggle.setAttribute('aria-expanded', 'false');
    });
    if (sortTrigger?.isConnected) sortTrigger.focus({ preventScroll: true });
    sortTrigger = null;
    return;
  }

  if (dialog.matches('[data-jj-filters]')) {
    unlockScroll(dialog);
    const root = dialog.closest('[data-jj-plp]');
    if (root) syncFiltersToggle(root);
    if (filtersTrigger?.isConnected) filtersTrigger.focus({ preventScroll: true });
    filtersTrigger = null;
  }
}

/* ----------------------------------------------------------------------------------------------
 * 3 / 4 columns
 * ------------------------------------------------------------------------------------------- */

/**
 * @param {Element} root
 * @param {string} view - '3' | '4'
 */
function applyView(root, view) {
  const grid = root.querySelector('.jj-plp__grid');
  grid?.setAttribute('product-grid-view', view === '3' ? 'three' : 'default');
  root.querySelectorAll('[data-jj-view]').forEach((button) => {
    button.setAttribute('aria-pressed', String(button.getAttribute('data-jj-view') === view));
  });
}

function restoreViews() {
  const view = desktopQuery.matches && sessionStorage.getItem(VIEW_KEY) === '3' ? '3' : '4';
  document.querySelectorAll('[data-jj-plp]').forEach((root) => applyView(root, view));
}

/* ----------------------------------------------------------------------------------------------
 * Price slider
 * ------------------------------------------------------------------------------------------- */

/**
 * Formats like the shop: the Liquid gives a sample (1234567 → «$1.234.567») and the number is
 * written in its place with the same thousands separator.
 * @param {HTMLElement} container
 * @param {number} value
 */
function formatPrice(container, value) {
  const sample = container.dataset.moneySample || '$1.234.567';
  const match = sample.match(/1(\D?)234\D?567/);
  if (!match) return String(value);
  const separator = match[1] || '';
  const digits = String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, separator);
  return sample.replace(match[0], digits);
}

/**
 * Keeps min ≤ max, updates the labels and the filled stretch, and writes the params the facets form
 * reads (empty at the ends of the range, so an untouched end doesn't filter).
 * @param {HTMLElement} container
 * @param {HTMLInputElement} [moved]
 */
function syncPrice(container, moved) {
  const min = container.querySelector('[data-jj-price-input="min"]');
  const max = container.querySelector('[data-jj-price-input="max"]');
  if (!(min instanceof HTMLInputElement) || !(max instanceof HTMLInputElement)) return;

  const top = Number(container.dataset.max) || Number(max.max) || 1;
  let from = Number(min.value);
  let to = Number(max.value);
  if (from > to) {
    if (moved === min) from = to;
    else to = from;
    min.value = String(from);
    max.value = String(to);
  }

  const minLabel = container.querySelector('[data-jj-price-label="min"]');
  const maxLabel = container.querySelector('[data-jj-price-label="max"]');
  if (minLabel) minLabel.textContent = formatPrice(container, from);
  if (maxLabel) maxLabel.textContent = formatPrice(container, to);

  const track = container.querySelector('.jj-price__track');
  if (track instanceof HTMLElement) {
    track.style.setProperty('--jj-price-from', `${(from / top) * 100}%`);
    track.style.setProperty('--jj-price-to', `${(to / top) * 100}%`);
  }

  const minParam = container.querySelector('[data-jj-price-param="min"]');
  const maxParam = container.querySelector('[data-jj-price-param="max"]');
  if (minParam instanceof HTMLInputElement) minParam.value = from > 0 ? String(from) : '';
  if (maxParam instanceof HTMLInputElement) maxParam.value = to < top ? String(to) : '';
}

/**
 * Capture phase on window: runs before Horizon's document-level listener reads the form.
 * @param {Event} event
 */
function onPriceInput(event) {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.matches('[data-jj-price-input]')) return;
  const container = input.closest('[data-jj-price]');
  if (container instanceof HTMLElement) syncPrice(container, input);
}

/* ----------------------------------------------------------------------------------------------
 * Loading line of the infinite scroll and the «updating results» state
 * ------------------------------------------------------------------------------------------- */

/**
 * The line goes away once the last page is in the grid. Any card of that page counts, not only the
 * last child: the products the infinite scroll recovers go after the cards of their page, and a page
 * added twice left a hidden repeated card of an earlier page at the end.
 * @param {Element} root
 */
function syncLoading(root) {
  const grid = root.querySelector('.jj-plp__grid');
  const loading = root.querySelector('[data-jj-loading]');
  if (!(grid instanceof HTMLElement) || !(loading instanceof HTMLElement)) return;
  const lastPage = Number(grid.dataset.lastPage);
  const done = Array.from(grid.children).some(
    (item) => item instanceof HTMLElement && Number(item.dataset.page) >= lastPage
  );
  loading.toggleAttribute('data-done', done);
}

/** @param {Event} event */
function onCollectionUpdate(event) {
  const target = event.target instanceof Element ? event.target : null;
  const section = target ? sectionOf(target) : null;
  if (!section || !section.querySelector('[data-jj-plp]')) return;

  section.setAttribute('data-jj-updating', '');
  const promise = /** @type {{ promise?: Promise<unknown> }} */ (event).promise;
  const done = () => {
    section.removeAttribute('data-jj-updating');
    const root = section.querySelector('[data-jj-plp]');
    if (!root) return;
    syncFiltersToggle(root);
    restoreViews();
    syncLoading(root);
    root.querySelectorAll('[data-jj-price]').forEach((container) => {
      if (container instanceof HTMLElement) syncPrice(container);
    });
  };
  afterRender(section, promise, done);
}

/**
 * Runs `done` once the section has been re-rendered: when the event's promise settles or, without
 * one, on the first change of the section's DOM (5s at most).
 * @param {Element} section
 * @param {Promise<unknown> | undefined} promise
 * @param {() => void} done
 */
function afterRender(section, promise, done) {
  if (promise && typeof promise.finally === 'function') {
    promise.finally(done);
    return;
  }
  let finished = false;
  const finish = () => {
    if (finished) return;
    finished = true;
    observer.disconnect();
    requestAnimationFrame(done);
  };
  const observer = new MutationObserver(finish);
  observer.observe(section, { childList: true, subtree: true });
  window.setTimeout(finish, 5000);
}

/* ----------------------------------------------------------------------------------------------
 * Wiring
 * ------------------------------------------------------------------------------------------- */

/** @param {MouseEvent} event */
function onClick(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const toggle = target.closest('[data-jj-filters-toggle]');
  if (toggle instanceof HTMLElement) {
    const root = toggle.closest('[data-jj-plp]');
    const panel = root?.querySelector('[data-jj-filters]');
    if (!root || !(panel instanceof HTMLDialogElement)) return;
    if (panel.open) closeFilters(panel);
    else openFilters(root, toggle);
    return;
  }

  const close = target.closest('[data-jj-filters-close]');
  if (close) {
    const panel = close.closest('[data-jj-filters]');
    if (panel instanceof HTMLDialogElement) closeFilters(panel);
    return;
  }

  const sortToggle = target.closest('[data-jj-sort-toggle]');
  if (sortToggle instanceof HTMLElement) {
    openSort(sortToggle);
    return;
  }

  const sortClose = target.closest('[data-jj-sort-close]');
  if (sortClose) {
    const panel = sortClose.closest('[data-jj-sort-panel]');
    if (panel instanceof HTMLDialogElement) closeDialog(panel);
    return;
  }

  const sortApply = target.closest('[data-jj-sort-apply]');
  if (sortApply) {
    const panel = sortApply.closest('[data-jj-sort-panel]');
    if (panel instanceof HTMLDialogElement) applySort(panel);
    return;
  }

  // Click on the backdrop of a modal (outside its box) closes it
  if (target instanceof HTMLDialogElement && target.matches('[data-jj-filters], [data-jj-sort-panel]')) {
    const box = target.getBoundingClientRect();
    const inside =
      event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
    if (!inside) closeDialog(target);
    return;
  }

  const view = target.closest('[data-jj-view]');
  if (view instanceof HTMLElement) {
    const root = view.closest('[data-jj-plp]');
    const value = view.dataset.jjView === '3' ? '3' : '4';
    sessionStorage.setItem(VIEW_KEY, value);
    if (root) applyView(root, value);
    return;
  }

  const more = target.closest('[data-jj-more]');
  if (more instanceof HTMLElement) {
    const content = more.closest('.jj-filters__content');
    const expanded = more.getAttribute('aria-expanded') !== 'true';
    content?.querySelectorAll('.is-extra').forEach((option) => option.toggleAttribute('hidden', !expanded));
    more.setAttribute('aria-expanded', String(expanded));
    more.textContent = (expanded ? more.dataset.labelLess : more.dataset.labelMore) || '';
    return;
  }
}

/**
 * Escape also closes the desktop sidebar (a non-modal dialog doesn't close by itself). Modals close
 * natively; an open «Ordenar por» panel takes the Escape first.
 * @param {KeyboardEvent} event
 */
function onKeyDown(event) {
  if (event.key !== 'Escape' || event.defaultPrevented || event.isComposing) return;
  const panel = document.querySelector('[data-jj-filters][open]');
  if (!(panel instanceof HTMLDialogElement)) return;
  if (document.querySelector('[data-jj-sort-panel][open]')) return;
  closeFilters(panel);
}

/**
 * Escape en los modales de mobile: también salen deslizando (el cancel nativo los cerraría de golpe).
 * @param {Event} event
 */
function onCancel(event) {
  const dialog = event.target;
  if (!(dialog instanceof HTMLDialogElement) || !dialog.matches('[data-jj-filters], [data-jj-sort-panel]')) return;
  if (desktopQuery.matches || prefersReducedMotion()) return;
  event.preventDefault();
  closeDialog(dialog);
}

/* ----------------------------------------------------------------------------------------------
 * Desktop sticky toolbar
 * ------------------------------------------------------------------------------------------- */

let stickyFrame = 0;

/**
 * La barra y el panel abierto se pegan bajo lo que el grupo del header deje fijo arriba: hoy nada en
 * desktop (el header se va con la página), pero si el header queda sticky o fixed, o se esconde al
 * bajar, se mide en cada scroll y la barra lo sigue. Escribe --jj-plp-sticky-top en <html>.
 */
function syncStickyTop() {
  stickyFrame = 0;
  let top = 0;
  const group = document.getElementById('header-group');
  if (group && desktopQuery.matches) {
    for (const element of [group, ...group.children]) {
      const { position } = getComputedStyle(element);
      if (position !== 'sticky' && position !== 'fixed') continue;
      const box = element.getBoundingClientRect();
      if (box.bottom > 0) top = Math.max(top, box.bottom);
    }
  }
  document.documentElement.style.setProperty('--jj-plp-sticky-top', `${Math.round(top)}px`);
}

function onScroll() {
  if (stickyFrame || !document.querySelector('.jj-plp__bar')) return;
  stickyFrame = requestAnimationFrame(syncStickyTop);
}

function onBreakpointChange() {
  document.querySelectorAll('[data-jj-filters][open], [data-jj-sort-panel][open]').forEach((dialog) => {
    if (dialog instanceof HTMLDialogElement) dialog.close();
  });
  restoreViews();
  syncStickyTop();
}

function init() {
  restoreViews();
  if (document.querySelector('.jj-plp__bar')) syncStickyTop();
  document.querySelectorAll('[data-jj-plp]').forEach((root) => {
    syncLoading(root);
    const grid = root.querySelector('.jj-plp__grid');
    if (grid && !(grid instanceof HTMLElement && grid.dataset.jjObserved)) {
      if (grid instanceof HTMLElement) grid.dataset.jjObserved = 'true';
      new MutationObserver(() => syncLoading(root)).observe(grid, { childList: true });
    }
  });
}

if (!window.jjCollectionGridReady) {
  window.jjCollectionGridReady = true;
  document.addEventListener('click', onClick);
  document.addEventListener('keydown', onKeyDown);
  document.addEventListener('close', onDialogClose, true);
  document.addEventListener('cancel', onCancel, true);
  // El scroll no burbujea: en captura llega el de .page-wrapper (desktop ≥990) y el del documento
  document.addEventListener('scroll', onScroll, { capture: true, passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  document.addEventListener('change', onSortChange);
  window.addEventListener('input', onPriceInput, true);
  window.addEventListener('change', onPriceInput, true);
  document.addEventListener(StandardEvents.collectionUpdate, onCollectionUpdate);
  // The search results (sections/jj-search-results.liquid) reuse this grid; facets.js announces their updates as search:update
  document.addEventListener(StandardEvents.searchUpdate, onCollectionUpdate);
  desktopQuery.addEventListener('change', onBreakpointChange);
  document.addEventListener('shopify:section:load', init);
}

init();
